import { zodResolver } from '@hookform/resolvers/zod';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { ApiHTTPError } from '@/lib/api/client';
import { toast } from '@/lib/notify';

import { useInviteMember } from '../api/useInviteMember';
import type { RoleOption } from '../api/useRoleOptions';
import { type InviteMemberInput, inviteMemberSchema } from '../schemas/memberSchemas';

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
  roles: RoleOption[];
}

export function InviteUserModal({ open, onClose, roles }: InviteUserModalProps) {
  const { mutate, isPending } = useInviteMember();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteMemberInput>({
    resolver: zodResolver(inviteMemberSchema),
    defaultValues: { email: '', roleId: '' },
  });

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  function onSubmit(data: InviteMemberInput) {
    mutate(data, {
      onSuccess: () => {
        toast.success('Invitation sent successfully');
        onClose();
      },
      onError: (err) => {
        const problem = (err as ApiHTTPError).data;
        const message =
          problem?.detail ?? problem?.errors?.[0]?.description ?? 'Failed to send invitation';
        toast.error(message);
      },
    });
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-sm rounded-3xl p-5 flex flex-col gap-4 bg-white border border-placeholder shadow-[0px_0px_18.6px_0px_#0000001A] animate-modal text-black-logo tracking-tight">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-base sm:text-lg lg:text-xl leading-6 font-semibold text-black tracking-normal">
            Invite User
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-6 h-6 rounded-full bg-[#E5EDFF] text-primary flex items-center justify-center shrink-0 hover:bg-[#d7e3ff] transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="invite-email" className="sc-input-label" data-required>
              Email Address
            </label>
            <input
              id="invite-email"
              type="email"
              className="sc-input"
              placeholder="john@example.com"
              {...register('email')}
            />
            {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="invite-role" className="sc-input-label" data-required>
              Role
            </label>
            <select
              id="invite-role"
              className="sc-select has-[option:disabled:checked]:text-placeholder"
              defaultValue=""
              {...register('roleId')}
            >
              <option value="" disabled>
                Select a role
              </option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            {errors.roleId && <p className="text-xs text-destructive">{errors.roleId.message}</p>}
          </div>

          <div className="flex sm:justify-end items-center gap-3.5 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outlined btn-small text-primary! hover:bg-primary/10!"
            >
              Cancel
            </button>
            <button type="submit" disabled={isPending} className="btn btn-primary btn-small">
              {isPending ? 'Sending…' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
